import React, { useState, useRef, useEffect } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import ContentContainer from '../layout/ContentContainer.jsx';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import { getTextDefaults } from './translations/SCORMSectionTranslation';
import { v4 } from 'uuid';

const PASSED_LESSON_STATUSES = ['completed', 'passed'];

const createDefaultData = () => ({
	// SCORM 1.2
	'cmi.core.student_id': v4(),
	'cmi.core.student_name': '    ',
	'cmi.core.lesson_status': 'not attempted',
	// SCORM 2004
	'cmi.learner_id': v4(),
	'cmi.learner_name': '    ',
	'cmi.completion_status': 'not attempted',
	'cmi.success_status': 'unknown',
	// normalized status for form navigation
	lesson_status: 'todo',
	session_time: '',
	'score.raw': 0,
	'score.min': 0,
	'score.max': 0,
});

/** Map SCORM 1.2 / 2004 CMI fields to internal lesson_status. */
const syncLessonStatus = (d) => {
	const status12 = d['cmi.core.lesson_status'];
	if (status12 === 'passed' || status12 === 'completed') {
		d.lesson_status = 'passed';
		return d;
	}
	if (status12 === 'failed') {
		d.lesson_status = 'failed';
		return d;
	}

	const success = d['cmi.success_status'];
	const completion = d['cmi.completion_status'];

	if (success === 'passed') {
		d.lesson_status = 'passed';
	} else if (success === 'failed') {
		d.lesson_status = 'failed';
	} else if (completion === 'completed') {
		d.lesson_status = 'passed';
	}

	return d;
};

const SCORMSection = ({
	name,
	title,
	introduction,
	navigationProps,
	url,
	startLabel,
	passedLabel,
	onChange,
	selectedLanguage = 'EN',
	...props
}) => {
	const [isReady, setReady] = useState(false);
	const [isExpanded, setExpanded] = useState(false);
	const [lessonStatus, setLessonStatus] = useState('todo');
	const dataRef = useRef(createDefaultData());
	let textDefaults = getTextDefaults(selectedLanguage);
	let text = Object.assign({}, textDefaults);
	const isPassed = PASSED_LESSON_STATUSES.includes(lessonStatus);

	const handleFullScreen = () => {
		window.parent.postMessage({ fullScreen: true }, '*');
		setExpanded(true);
	};

	const closeFullScreen = (force) => {
		if (!force && !isPassed && !confirm(text.confirmClosure)) return;
		window.parent.postMessage({ fullScreen: false }, '*');
		setExpanded(false);
	};

	const applyDataUpdate = (d) => {
		syncLessonStatus(d);
		dataRef.current = d;
		setLessonStatus(d.lesson_status);
		if (onChange && name) onChange({ target: { name, value: d } });
		return d;
	};

	const handleFinish = () => {
		console.log('handleFinish');
		applyDataUpdate({ ...dataRef.current });
		closeFullScreen(true);
	};

	const handleValue = (k, v) => {
		console.log('LMSSetValue', k, v);
		const d = { ...dataRef.current, [k]: v };
		const key = k.replace('cmi.', '').replace('core.', '');
		d[key] = v;
		applyDataUpdate(d);
		if (d.lesson_status === 'passed') closeFullScreen(true);
		return true;
	};

	const handlersRef = useRef({ handleValue, handleFinish });
	handlersRef.current = { handleValue, handleFinish };

	// https://scorm.com/scorm-explained/technical-scorm/run-time/
	useEffect(() => {
		const getValue = (key) => dataRef.current[key] ?? '';

		// SCORM 1.2
		window.API = {
			LMSInitialize: (d) => {
				console.log('LMSInitialize', d);
				return true;
			},
			LMSFinish: () => handlersRef.current.handleFinish(),
			LMSGetValue: (d) => {
				console.log('LMSGetValue', d, getValue(d));
				return getValue(d);
			},
			LMSSetValue: (k, v) => handlersRef.current.handleValue(k, v),
			LMSCommit: (d) => {
				console.log('LMSCommit', d);
				return true;
			},
			LMSGetLastError: (d) => {
				console.log('LMSGetLastError');
				return 0;
			},
			LMSGetErrorString: (d) => {
				console.log('LMSGetErrorString');
				return 0;
			},
			LMSGetDiagnostic: (d) => {
				console.log('LMSGetDiagnostic');
				return 0;
			},
		};

		// SCORM 2004
		window.API_1484_11 = {
			Initialize: (d) => {
				console.log('Initialize', d);
				return true;
			},
			Finish: (d) => {
				console.log('Finish', d);
				handlersRef.current.handleFinish();
			},
			GetValue: (d) => {
				console.log('GetValue', d, getValue(d));
				return getValue(d);
			},
			SetValue: (k, v) => handlersRef.current.handleValue(k, v),
			Commit: (d) => {
				console.log('Commit', d);
				return true;
			},
			Terminate: (d) => {
				console.log('Terminate', d);
				handlersRef.current.handleFinish();
			},
			GetLastError: (d) => {
				console.log('GetLastError');
				return 0;
			},
			GetErrorString: (d) => {
				console.log('GetErrorString');
				return 0;
			},
			GetDiagnostic: (d) => {
				console.log('GetDiagnostic');
				return 0;
			},
		};

		setReady(true);

		return () => {
			delete window.API;
			delete window.API_1484_11;
		};
	}, []);

	return (
		<SectionContainer Navigation={<SectionNav {...navigationProps} canContinue={isPassed} />}>
			<ContentContainer>
				<Typography variant="h6">{title}</Typography>
				{introduction && <Typography>{introduction}</Typography>}

				{!isPassed && (
					<Button
						variant="contained"
						endIcon={<PlayArrowIcon />}
						style={{ width: '100%', height: 100 }}
						onClick={handleFullScreen}
					>
						{startLabel || text.start}
					</Button>
				)}

				{isPassed && (
					<Box sx={{ width: '100%', height: 100, textAlign: 'center', color: 'success.main' }}>
						<CheckCircleIcon style={{ fontSize: 100 }} color="success" />
						<Typography variant="h5">{passedLabel || 'All done!'}</Typography>
					</Box>
				)}

				<Dialog fullScreen open={isExpanded} onClose={closeFullScreen}>
					{isReady && (
						<iframe
							title="SCORM"
							allow="camera; microphone"
							style={{ width: '100%', height: '100%', border: 'none' }}
							src={url}
							{...props}
						/>
					)}

					<Fab
						color="primary"
						size="small"
						style={{ position: 'absolute', left: 0, top: '50%', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
						onClick={() => closeFullScreen()}
					>
						<ArrowBackIcon />
					</Fab>
				</Dialog>
			</ContentContainer>
		</SectionContainer>
	);
};

SCORMSection.type = 'SCORMSection';
SCORMSection.hasNavigation = true;
SCORMSection.spec = [
	{
		name: 'SCORMSection',
		key: 'SCORMSection',
		title: 'Collection of fields',
		translatable: [
			'startLabel',
			'passedLabel',
			'url',
			'navigation.previousLabel',
			'navigation.nextLabel',
			'navigation.submitLabel',
		],
		fields: [
			{
				type: 'TextField',
				name: 'startLabel',
				label: 'Label to start SCORM',
			},
			{
				type: 'TextField',
				name: 'passedLabel',
				label: 'Label on passing SCORM',
			},
			{
				type: 'TextField',
				name: 'url',
				label: 'URL to the SCORM package',
			},
			{
				type: 'TextField',
				name: 'navigation.previousLabel',
				label: 'Previous label',
				helperText: 'if there is a previous section, this will be the label to go back.',
			},
			{
				type: 'TextField',
				name: 'navigation.nextLabel',
				label: 'Next label',
				helperText: 'if there is a next section, this will be the label to go forward.',
			},
			{
				type: 'TextField',
				name: 'navigation.submitLabel',
				label: 'Submit label',
				helperText: 'If this section is the last section, this will be the text on the submit button.',
			},
		],
		defaultProperties: {
			title: 'Section title',
			navigation: {
				previousLabel: 'Previous',
				nextLabel: 'Next',
				submitLabel: 'Submit',
			},
		},
	},
];

export default SCORMSection;
